/**
 * SystemSettingsProvider
 * Contain most logic handing with Omnitech Api
 */

import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react'
import _ from 'lodash'

import { CacheChecksumContext, createPersistedState } from '../../cache'
import SystemSettingsContext from './system-settings-context'
import { fetchSystemSettings } from './system-setting-api'
import { cancelRequest } from '../../helpers'
import config from '../../config'
import { useTenants } from '../../tenants'

// create use state hook with persisted storage
// TODO: support using different storage provider
const useSystemSettingsState = createPersistedState('systemSettings')
const useSystemSettingsChecksumState = createPersistedState('systemSettingsChecksum')

export default function SystemSettingsProvider({ children }) {
  // handle system setting cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('systemSettings')

  // manage state for system setting and checksum
  const [systemSettings, setSystemSettings] = useSystemSettingsState({})
  const [systemSettingsChecksum, setSystemSettingsChecksum] = useSystemSettingsChecksumState('')
  const [systemSettingsReady, setSystemSettingsReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  const { tenant } = useTenants()

  /**
   * get system setting from omnitech API when app is mounted
   */
  useEffect(() => {
    // use system setting in cache first
    if (!_.isEmpty(systemSettings)) {
      setSystemSettingsReady(true)
      setEnableRefresh(true)
      return
    }

    const promise = fetchSystemSettings()
    promise
      .then(({ cacheChecksum, systemSettings }) => {
        setSystemSettings(systemSettings)
        setSystemSettingsReady(true)
        setEnableRefresh(true)
        setSystemSettingsChecksum(cacheChecksum)
      })
      .catch(error => {
        // TODO: handle error
      })
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchSystemSettings'])
    }
  }, [])

  // update system setting cache base on cache checksum
  useEffect(() => {
    if (!systemSettingsReady || _.isUndefined(cacheChecksumValue)) return

    // when it is not required to update cache, skip the process
    if (!enableRefresh || systemSettingsChecksum === cacheChecksumValue) {
      if (_.isEmpty(systemSettingsChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setSystemSettingsChecksum(cacheChecksumValue)
      }
      return
    }

    // start refreshing system setting with omnitech API
    setEnableRefresh(false)
    const promise = fetchSystemSettings()
    promise
      .then(({ cacheChecksum, systemSettings }) => {
        setSystemSettings(systemSettings)
        setSystemSettingsChecksum(cacheChecksum)
      })
      .catch(error => {
        // TODO: handle error
        console.warn('error: ', error)
      })
      .finally(() => {
        setEnableRefresh(true)
      })
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchSystemSettings'])
    }
  }, [systemSettingsChecksum, systemSettingsReady, cacheChecksumValue])

  const tenantSystemSettingsOverrides = useMemo(() => (
    _.get(tenant, 'frontendConfig.systemSettings', [])
  ), [tenant])

  // get system setting by key,
  const getSystemSetting = useCallback((key, defaultValue) => {
    let setting = _.find(tenantSystemSettingsOverrides, { name: key })
    if (_.isEmpty(setting)) {
      setting = _.find(systemSettings, { name: key })
    }
    return _.get(setting, 'value', defaultValue)
  }, [systemSettings, tenantSystemSettingsOverrides])

  const state = {
    systemSettings,
    systemSettingsReady,
    getSystemSetting
  }
  return (
    <SystemSettingsContext.Provider value={state}>
      {children}
    </SystemSettingsContext.Provider>
  )
}
